import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { firestore, writeBatch, collection, query, where, getDocs, setDoc, addDoc, doc } from '../config/firebaseConfig'; // Certifique-se de incluir 'doc' aqui
import './ImportContactsModal.css';

function ImportContactsModal({ onClose, currentUser }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recordsProcessed, setRecordsProcessed] = useState(0);
  const [recordsIgnored, setRecordsIgnored] = useState(0);

  // Buscar tenantId do currentUser ou do localStorage
  const tenantId = currentUser?.tenantId || localStorage.getItem('tenantId');

  if (!tenantId) {
    setError('Erro: tenantId não encontrado. Não é possível continuar com a importação.');
    return null; // Não exibe a interface de importação se o tenantId estiver ausente
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const sanitizeId = (id) => {
    return id.replace(/[/]/g, '_');
  };

  const isValidDate = (dateStr) => {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return dateRegex.test(dateStr);
  };

  const convertDateFormat = (dateStr) => {
    if (!isValidDate(dateStr)) {
      return null;
    }
    const parts = dateStr.split('/');
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`;
  };

  const handleImport = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);
    setRecordsProcessed(0);
    setRecordsIgnored(0);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!data || data.length === 0) {
          setError('O arquivo está vazio ou não possui dados válidos.');
          setLoading(false);
          return;
        }

        const batch = writeBatch(firestore);
        const contactsCollectionRef = collection(firestore, 'contatos');
        const historyCollectionRef = collection(firestore, 'historicoAluno');
        const logCollectionRef = collection(firestore, 'importLogs');
        const timestamp = new Date().toISOString();
        const user = currentUser ? currentUser.email : 'unknown';

        let recordsInserted = 0;
        let recordsUpdated = 0;

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          setRecordsProcessed(i + 1);

          // Converte a data de nascimento, se existir
          if (item['dataNascimento']) {
            const convertedDate = convertDateFormat(item['dataNascimento']);
            if (convertedDate) {
              item['dataNascimento'] = convertedDate;
            } else {
              setRecordsIgnored(prev => prev + 1);
              continue;
            }
          }

          const emailKey = Object.keys(item).find(key => key.toLowerCase().includes('email'));
          if (!emailKey) {
            setRecordsIgnored(prev => prev + 1);
            continue;
          }

          const email = item[emailKey];
          if (!email) {
            setRecordsIgnored(prev => prev + 1);
            continue;
          }

          const { curso, turma, ...contactData } = item;
          const emailQuery = query(contactsCollectionRef, where('email', '==', email), where('tenantId', '==', tenantId));
          const existingDocs = await getDocs(emailQuery);

          if (existingDocs.empty) {
            const itemWithTimestamp = { 
              ...contactData, 
              tenantId, // Associar o tenantId
              dataCriacao: timestamp, 
              dataUltimaAtualizacao: timestamp,
              usuarioAlteracao: user,
              tipoContato: curso && turma ? 'Aluno' : 'Lead',
              origem: 'Importação',
              status: 'Pendente',
            };
            // Usar addDoc para deixar o Firestore gerar o ID automaticamente
            await addDoc(contactsCollectionRef, itemWithTimestamp);
            recordsInserted += 1;
          } else {
            existingDocs.forEach((doc) => {
              const itemRef = doc.ref;
              const existingData = doc.data();
              const hasChanges = Object.keys(contactData).some(key => contactData[key] !== existingData[key]);

              if (hasChanges) {
                const updatedItem = { 
                  ...contactData, 
                  tenantId, // Garantir que o tenantId esteja presente
                  dataUltimaAtualizacao: timestamp, 
                  usuarioAlteracao: user,
                  tipoContato: curso && turma ? 'Aluno' : 'Lead'
                };
                batch.set(itemRef, updatedItem, { merge: true });
                recordsUpdated += 1;
              } else {
                setRecordsIgnored(prev => prev + 1);
              }
            });
          }

          if (curso && turma) {
            const sanitizedEmail = sanitizeId(email);
            const sanitizedCurso = sanitizeId(curso);
            const sanitizedTurma = sanitizeId(turma);
            const historyDocId = `${sanitizedEmail}_${sanitizedCurso}_${sanitizedTurma}`;
            const historyRef = doc(historyCollectionRef, historyDocId);
            const historyQuery = query(historyCollectionRef, where('email', '==', email), where('curso', '==', curso), where('turma', '==', turma), where('tenantId', '==', tenantId));
            const existingHistoryDocs = await getDocs(historyQuery);

            if (existingHistoryDocs.empty) {
              const historyData = {
                email,
                curso,
                turma,
                tenantId, // Associar tenantId ao histórico também
                dataAlteracao: timestamp
              };
              await setDoc(historyRef, historyData);
            }
          }

          setProgress(((i + 1) / data.length) * 100);
        }

        await batch.commit();
        setSuccess('Contatos importados com sucesso!');

        const logData = {
          tenantId, // Log de importação com tenantId
          dataImportacao: timestamp,
          registrosProcessados: data.length,
          registrosInseridos: recordsInserted,
          registrosAtualizados: recordsUpdated,
          registrosIgnorados: recordsIgnored,
          usuarioImportacao: user
        };

        await setDoc(doc(logCollectionRef, timestamp), logData);
      } catch (error) {
        setError('Erro ao importar contatos. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <div className="import-contacts-modal">
      <div className="import-contacts-modal-content">
        <h3>Importar Contatos</h3>
        <input
          type="file"
          accept=".xlsx"
          onChange={handleFileChange}
        />
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
        {loading && (
          <>
            <div className="progress-bar">
              <div className="progress-bar-filled" style={{ width: `${progress}%` }}>
                {Math.round(progress)}%
              </div>
            </div>
            <div className="import-contacts-stats">
              <span>Registros Processados: {recordsProcessed}</span>
              <span>Registros Ignorados: {recordsIgnored}</span>
              <span>Importação: {recordsProcessed} de {recordsProcessed + recordsIgnored}</span>
            </div>
          </>
        )}
        <button onClick={handleImport} disabled={loading}>
          {loading ? 'Importando...' : 'Importar'}
        </button>
        <button onClick={onClose} className="cancel">
          Fechar
        </button>
      </div>
    </div>
  );
}

export default ImportContactsModal;
